// A squall over the profile ship (every 5th click on it, all visitors
// together — see recordShipClick in api/src/profiles/broadsides.ts): the light
// goes grey, thick clouds roll in, rain slants in on the wind, the sea heaves (setSeaStorm), the
// ship labours, and far-off lightning flickers just ahead of the thunder in
// /audio/storm.m4a — a softly muffled wind ("Whistling of the wind #2") and a
// distant "Thunder #1", both CC0 from bigsoundbank.com. It all passes in
// STORM_SECONDS. Kept subtle on purpose: the sound is low, the lightning a
// glow on the horizon rather than a white flash.

import { setSeaStorm } from './BannerCanvas.js';
import { reduceMotion } from './motion.js';

export const STORM_SECONDS = 16;
/** When the lightning shows, in seconds: just ahead of the claps in storm.m4a (light outruns sound). */
const LIGHTNING = [1.75, 3.8, 6.8, 8.9];

let active = false;

/** Plays the storm's sound. Only call from a click. */
function playStormSound(): void {
  try {
    const audio = new Audio('/audio/storm.m4a');
    audio.volume = 0.9;
    void audio.play().catch(() => {});
  } catch {
    // no sound is fine
  }
}

/**
 * One bank of storm cloud, drawn once: a mass of soft, overlapping lumps,
 * billowing on top, heavy and darker underneath (where no light reaches),
 * `w` × `h` px, in greys meant for multiplying over the page.
 */
function cloudBank(w: number, h: number, darkness: number, density = 1): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  const lump = (x: number, y: number, r: number, shade: number, a: number) => {
    const alpha = Math.min(0.95, a * density);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(${shade},${shade + 3},${shade + 7},${alpha})`);
    grad.addColorStop(0.65, `rgba(${shade},${shade + 3},${shade + 7},${alpha * 0.45})`);
    grad.addColorStop(1, `rgba(${shade},${shade + 3},${shade + 7},0)`);
    g.fillStyle = grad;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  };
  // lumps stay clear of the ends, which are feathered below so banks blend into each other
  const lx = () => w * (0.08 + Math.random() * 0.84);
  // billowing tops: lighter lumps along an uneven upper line
  for (let i = 0; i < Math.round(70 * density); i++) {
    const x = lx();
    const top = h * (0.35 + 0.12 * Math.sin(x / w * Math.PI * 3 + Math.random()));
    lump(x, top + Math.random() * h * 0.15, h * (0.14 + Math.random() * 0.16), 150 - darkness * 30, 0.22);
  }
  // the body
  for (let i = 0; i < Math.round(90 * density); i++) {
    lump(lx(), h * (0.5 + Math.random() * 0.2), h * (0.16 + Math.random() * 0.18), 125 - darkness * 35, 0.2);
  }
  // the heavy, dark underside
  for (let i = 0; i < Math.round(60 * density); i++) {
    lump(lx(), h * (0.66 + Math.random() * 0.12), h * (0.1 + Math.random() * 0.12), 95 - darkness * 30, 0.24);
  }
  // feather both ends so no straight edge ever shows
  const fade = g.createLinearGradient(0, 0, w, 0);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(0.2, 'rgba(0,0,0,1)');
  fade.addColorStop(0.8, 'rgba(0,0,0,1)');
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  g.globalCompositeOperation = 'destination-in';
  g.fillStyle = fade;
  g.fillRect(0, 0, w, h);
  g.globalCompositeOperation = 'source-over';
  return c;
}

/**
 * Thick clouds rolling in from the right on the wind, in three layers (far
 * ones paler, higher and slower), as dense as `level()` says (0..1).
 */
function clouds(canvas: HTMLCanvasElement, level: () => number, until: number): void {
  const g = canvas.getContext('2d');
  if (!g) return;
  const r = canvas.getBoundingClientRect();
  const w = (canvas.width = Math.max(1, Math.round(r.width)));
  const h = (canvas.height = Math.max(1, Math.round(r.height)));
  const layers = [
    // far back: an almost solid ceiling of cloud that closes the whole sky
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.75), 0.2, 2.4), top: -0.12, speed: 5, x: w * 0.05 },
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.48), 0.3, 1.7), top: -0.04, speed: 9, x: w * 0.25 },
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.52), 0.6, 1.5), top: 0.03, speed: 16, x: w * 0.55 },
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.55), 1, 1.3), top: 0.1, speed: 26, x: w * 0.85 },
    // low scud right down on the horizon: the sea (drawn in front) cuts it off at the water
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.42), 0.8, 1.6), top: 0.36, speed: 34, x: w * 0.4 },
    { bank: cloudBank(Math.round(w * 1.4), Math.round(h * 0.4), 0.6, 1.8), top: 0.5, speed: 40, x: w * 0.7 },
  ];
  let last = 0;
  const step = (now: number) => {
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
    last = now;
    const dense = level();
    g.clearRect(0, 0, w, h);
    for (const layer of layers) {
      // rolling in: while it gathers the banks also sweep in from the right edge
      layer.x -= (layer.speed + (1 - dense) * 60) * dt;
      // banks follow each other overlapping by their feathered ends
      const stride = layer.bank.width * 0.8;
      if (layer.x < -stride) layer.x += stride;
      g.globalAlpha = dense;
      for (let x = layer.x - stride; x < w; x += stride) g.drawImage(layer.bank, x, layer.top * h);
    }
    g.globalAlpha = 1;
    if (performance.now() < until) requestAnimationFrame(step);
    else g.clearRect(0, 0, w, h);
  };
  requestAnimationFrame(step);
}

/** Slanting rain on a canvas over the area, as heavy as `level()` says (0..1). */
function rain(canvas: HTMLCanvasElement, level: () => number, until: number): void {
  const g = canvas.getContext('2d');
  if (!g) return;
  const fit = () => {
    const r = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(r.width));
    canvas.height = Math.max(1, Math.round(r.height));
  };
  fit();
  const w = () => canvas.width;
  const h = () => canvas.height;
  // the wind drives it in from the right, towards the bow
  const slant = -0.32;
  const streaks = Array.from({ length: 260 }, () => ({
    x: Math.random() * w() * 1.3,
    y: Math.random() * h(),
    len: 12 + Math.random() * 16,
    speed: 650 + Math.random() * 420,
    alpha: 0.12 + Math.random() * 0.22,
  }));
  let last = 0;
  const step = (now: number) => {
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
    last = now;
    const heavy = level();
    g.clearRect(0, 0, w(), h());
    g.lineCap = 'round';
    g.lineWidth = 1.1;
    const shown = Math.round(streaks.length * heavy);
    for (let i = 0; i < streaks.length; i++) {
      const s = streaks[i]!;
      s.y += s.speed * dt;
      s.x += s.speed * slant * dt;
      if (s.y > h() || s.x < -40) {
        s.y = -s.len - Math.random() * 40;
        s.x = Math.random() * w() * 1.3;
      }
      if (i >= shown) continue;
      g.strokeStyle = `rgba(70,82,92,${s.alpha})`;
      g.beginPath();
      g.moveTo(s.x, s.y);
      g.lineTo(s.x - s.len * slant, s.y - s.len);
      g.stroke();
    }
    if (performance.now() < until) requestAnimationFrame(step);
    else g.clearRect(0, 0, w(), h());
  };
  requestAnimationFrame(step);
}

/**
 * Brings a squall over `area` (the ship's section). Does nothing while one is
 * already blowing. Resolves when it has passed.
 */
export function startStorm(area: HTMLElement): Promise<void> {
  if (active) return Promise.resolve();
  active = true;
  return new Promise((resolve) => {
    const start = performance.now();
    const end = start + STORM_SECONDS * 1000;
    // how hard it blows: rises over 2.5 s, holds, eases off over the last 4.5 s
    const level = () => {
      const t = (performance.now() - start) / 1000;
      return Math.max(0, Math.min(1, t / 2.5, (STORM_SECONDS - t) / 4.5));
    };

    playStormSound();
    area.classList.add('is-storm');
    if (!reduceMotion) setSeaStorm(1);

    const sky = document.createElement('canvas');
    sky.className = 'pf-clouds';
    sky.setAttribute('aria-hidden', 'true');
    // behind everything in the area: the first thing in it
    area.prepend(sky);
    clouds(sky, level, end);

    const canvas = document.createElement('canvas');
    canvas.className = 'pf-rain';
    canvas.setAttribute('aria-hidden', 'true');
    const flash = document.createElement('div');
    flash.className = 'pf-lightning';
    flash.setAttribute('aria-hidden', 'true');
    area.append(flash);
    if (!reduceMotion) {
      area.append(canvas);
      rain(canvas, level, end);
      // sheet lightning far off: a glow, never a white-out
      for (const at of LIGHTNING) {
        window.setTimeout(() => {
          flash.classList.remove('is-flashing');
          void flash.offsetWidth;
          flash.classList.add('is-flashing');
        }, at * 1000);
      }
    }

    // the sea settles a little before the sky clears
    window.setTimeout(() => setSeaStorm(0), (STORM_SECONDS - 5) * 1000);
    window.setTimeout(() => area.classList.remove('is-storm'), (STORM_SECONDS - 4) * 1000);
    window.setTimeout(() => {
      canvas.remove();
      sky.remove();
      flash.remove();
      active = false;
      resolve();
    }, STORM_SECONDS * 1000);
  });
}
